/**
 * Route tree of the application. Auth state and guards live in
 * `src/auth/AuthContext.tsx` and `src/routes/guards.tsx`.
 */
import { Navigate, Route, Routes } from 'react-router-dom';

import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { PendingPage } from './pages/PendingPage';
import { VerifyPage } from './pages/VerifyPage';
import { InstallPrompt } from './pwa/InstallPrompt';
import { UpdatePrompt } from './pwa/UpdatePrompt';
import { adminRouteElement } from './features/admin/routes';
import { householdRoutes } from './features/household/routes';
import { sessionRoutes } from './features/session/routes';
import { AppShell } from './routes/AppShell';
import { RedirectIfFullyAuthenticated, RequireAuth, RequireFullAccess } from './routes/guards';

/** Layout route wrapping the main app shell in the full-access guard. */
function ProtectedShell() {
  return (
    <RequireFullAccess>
      <AppShell />
    </RequireFullAccess>
  );
}

/**
 * Render the whole application: the PWA banners, then the routed page.
 *
 * @returns The application element.
 */
export function App() {
  return (
    <>
      <UpdatePrompt />
      <InstallPrompt />
      <Routes>
        <Route
          path="/login"
          element={
            <RedirectIfFullyAuthenticated>
              <LoginPage />
            </RedirectIfFullyAuthenticated>
          }
        />
        <Route path="/auth/verify" element={<VerifyPage />} />
        {/* Legacy path kept so links built before the fix keep working. */}
        <Route path="/verify" element={<VerifyPage />} />
        <Route
          path="/onboarding"
          element={
            <RequireAuth>
              <OnboardingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/pending"
          element={
            <RequireAuth>
              <PendingPage />
            </RequireAuth>
          }
        />

        <Route element={<ProtectedShell />}>
          <Route path="/" element={<HomePage />} />
          {sessionRoutes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
          {householdRoutes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
        </Route>

        {adminRouteElement}

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
